import "./ChangePinForm.css";

import classNames from "classnames";
import PropTypes from "prop-types";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/Base/Button/Button";
import { LabelInput } from "~/components/Base/LabelInput/LabelInput";
import { pinCodeFormat } from "~/helpers/format-helpers";
import { eraseCookie } from "~/helpers/cookies";
import { StatusMessage } from "~/components/Base/StatusMessage/StatusMessage";
import { useSendChangePin } from "~/hooks/api/account/useSendChangePin";

export function ChangePinForm({ className }) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { sendChangePin, isSending } = useSendChangePin();

  const [newPinCode, setNewPinCode] = useState("");
  const [hasJustInitiated, setHasJustInitiated] = useState(false);
  const [newPinCodeRepeat, setNewPinCodeRepeat] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetFormStatus = () => {
    setError("");
    setSuccess("");
  };

  const changePin = async (e) => {
    e.preventDefault();

    if (isSending || !validatePinCodes()) {
      return false;
    }

    try {
      const result = await sendChangePin({
        PinCode: newPinCode,
        Repeat: newPinCodeRepeat,
      });

      if (result?.success) {
        eraseCookie(import.meta.env.VITE_CHANGE_PIN_COOKIE_NAME);
        setHasJustInitiated(result?.sentReference);
        setSuccess(t("change_pin.successful_change"));
      } else {
        setError(t("change_pin.change_failed")); // Default to most likely error is message missing
      }
    } catch (e) {
      setError(e.message ?? t("change_pin.change_failed")); // Default to most likely error is message missing
    }
  };

  const redirectToStart = (e) => {
    e.preventDefault();
    if (hasJustInitiated){
      navigate('/welcome');
    } else{
      navigate("/");
    }
  };

  const validatePinCodes = () => {
    if (newPinCode !== newPinCodeRepeat) {
      setError(t("change_pin.no_match"));
      return false;
    }

    return true;
  };

  const disableForm = isSending || !!success; // Form is disabled while sending and after success

  return (
    <form
      className={classNames({
        "change-pin-form": true,
        [className]: !!className,
      })}
      onSubmit={changePin}
      aria-label={t("change_pin.new_pin")}
    >
      <LabelInput
        id="new-pincode"
        type="password"
        pattern={pinCodeFormat}
        required={true}
        ariaDescribedBy="change-pin-details"
        value={newPinCode}
        onChange={(e) => {
          resetFormStatus();
          setNewPinCode(e.target.value);
        }}
        label={t("change_pin.new_pin")}
        className="change-pin-form__row change-pin-form__input"
        disabled={disableForm}
      />

      <LabelInput
        id="new-pincode-repeat"
        type="password"
        pattern={pinCodeFormat}
        required={true}
        ariaDescribedBy="change-pin-details"
        value={newPinCodeRepeat}
        onChange={(e) => {
          resetFormStatus();
          setNewPinCodeRepeat(e.target.value);
        }}
        label={t("change_pin.repeat_new_pin")}
        className="change-pin-form__row change-pin-form__input"
        disabled={disableForm}
      />

      <StatusMessage
        id="change-pin-details"
        className="change-pin-form__details"
        isError={!!error}
        isSuccess={!!success}
        message={
          error
            ? error
            : success
            ? success
            : t("change_pin.valid_format", { pinCodeDigits: 4 })
        }
      />

      <div className="change-pin-form__row change-pin-form__buttons">
        <Button
          size="large"
          color="orange"
          label={t("change_pin.submit")}
          disabled={disableForm}
          buttonType="submit"
        />
        {success.length > 0 && (
          <Button
            size="large"
            color="green"
            label={t("change_pin.continue")}
            onClick={redirectToStart}
          />
        )}
      </div>
    </form>
  );
}

ChangePinForm.propTypes = {
  className: PropTypes.string,
};
